import { useState } from 'react';
import { api, type MappingSuggestion } from '../api-client';
import { tenantStore } from '../lib/tenant-store';

const DEFAULT_BCART_FIELDS = `order.customer.customerCode
order.customer.companyName
order.customer.contactName
order.items[].productCode
order.items[].name
order.items[].quantity
order.items[].unitPrice
order.items[].taxRate
order.totals.grandTotal
order.orderedAt`;

const DEFAULT_TARGET_SAMPLE = `伝票区分,伝票日付,得意先コード,得意先名,商品コード,商品名,数量,単価,金額,税率
21,20260520,A001,株式会社サンプル,ITEM-A,商品A,2,5000,10000,10`;

export default function AiMapping() {
  const tenantId = tenantStore.get() ?? '';
  const [targetName, setTargetName] = useState('弥生販売 受注インポート');
  const [bcartFields, setBcartFields] = useState(DEFAULT_BCART_FIELDS);
  const [targetSample, setTargetSample] = useState(DEFAULT_TARGET_SAMPLE);
  const [result, setResult] = useState<MappingSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tenantId) return <p>先にテナントを設定してください</p>;

  async function handleRun() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.suggestMapping({
        tenantId,
        sourceFields: bcartFields.split('\n').map((s) => s.trim()).filter(Boolean),
        targetSample: targetSample.split('\n').map((s) => s.trim()).filter(Boolean),
        targetName,
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">AIマッピング支援</h2>
      <p className="text-sm text-slate-600 mb-6">
        連携先のCSVサンプルとBcart項目を与えると、AIが自動でマッピング候補を提案します（Claude API使用）
      </p>

      <div className="bg-white rounded shadow border p-4 space-y-4 max-w-3xl">
        <div>
          <label className="block text-sm font-medium mb-1">連携先名</label>
          <input
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bcart項目（1行1項目）</label>
          <textarea
            value={bcartFields}
            onChange={(e) => setBcartFields(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border rounded font-mono text-xs"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">連携先CSVサンプル（ヘッダ＋データ）</label>
          <textarea
            value={targetSample}
            onChange={(e) => setTargetSample(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border rounded font-mono text-xs"
          />
        </div>

        <button
          disabled={loading}
          onClick={handleRun}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? '推論中…' : 'AIマッピング推論'}
        </button>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        {result && (
          <div className="space-y-3">
            <h3 className="font-semibold">提案 ({result.candidates.length}件)</h3>
            <table className="w-full text-sm bg-slate-50">
              <thead className="bg-slate-200">
                <tr>
                  <th className="px-2 py-1 text-left">連携先項目</th>
                  <th className="px-2 py-1 text-left">Bcart項目</th>
                  <th className="px-2 py-1 text-left">確信度</th>
                  <th className="px-2 py-1 text-left">根拠</th>
                </tr>
              </thead>
              <tbody>
                {result.candidates.map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1 font-mono">{c.targetField}</td>
                    <td className="px-2 py-1 font-mono text-blue-700">{c.sourceField}</td>
                    <td className="px-2 py-1">
                      <span
                        className={
                          c.confidence > 0.8
                            ? 'text-green-700'
                            : c.confidence > 0.5
                              ? 'text-amber-700'
                              : 'text-red-700'
                        }
                      >
                        {Math.round(c.confidence * 100)}%
                      </span>
                    </td>
                    <td className="px-2 py-1 text-xs text-slate-600">{c.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {result.unmappedFields.length > 0 && (
              <div className="bg-amber-50 p-3 rounded">
                <p className="font-semibold text-amber-800 text-sm mb-1">マッピング不能項目</p>
                <ul className="text-xs">
                  {result.unmappedFields.map((f) => (
                    <li key={f}>・{f}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-slate-500">
              使用トークン: {result.totalTokensUsed} / 推定コスト: ¥{result.estimatedCostJpy}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
